import MapboxDraw, { DrawCustomModeThis, DrawFeature, MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { expression } from "@mapbox/mapbox-gl-style-spec";
import { Feature } from "geojson";
import { Utils } from "mapbox-gl-draw-snap-mode";

import { EditingLayer } from "fond/types/mapboxDraw";
import { assertNever } from "fond/utils/assertNever";

import { ValidationState } from "../../validation/helper";

const {
  constants: Constants,
  lib: {
    CommonSelectors: { isOfMetaType },
  },
} = MapboxDraw;

const isVertex = isOfMetaType(Constants.meta.VERTEX);
const isMidpoint = isOfMetaType(Constants.meta.MIDPOINT);

/**
 * Evaluates a Mapbox filter expression against a feature.
 * Returns true if the feature matches the filter, false otherwise.
 * Silently handles evaluation errors (e.g., null values) by returning false.
 *
 * @param filterExpression - The Mapbox filter expression to evaluate
 * @param feature - The GeoJSON feature to test against the filter
 * @returns true if the feature matches the filter, false otherwise
 */
export function evaluateFilterExpression(filterExpression: unknown[], feature: Feature): boolean {
  try {
    const expr = expression.createExpression(filterExpression, { type: "boolean" });
    if (expr.result === "success") {
      try {
        const result = expr.value.evaluate({ zoom: 0 }, feature);
        // Mapbox expressions can return null (e.g., when comparing null values)
        // Treat null as false to exclude features with invalid data from sublayers
        return result === null ? false : result;
      } catch (evalError) {
        // Evaluation failed (e.g., null value where number expected)
        // Exclude feature from sublayer
        return false;
      }
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

export type ColocatedFeature =
  | { id: string; type: "Point" }
  | { id: string; type: "LineString"; vertexIndex: number }
  | { id: string; type: "Polygon"; ringIndex: number; coordIndex: number }
  | { id: string; type: "MultiPolygon"; polygonIndex: number; ringIndex: number; coordIndex: number };

type PolygonCoords = Array<Array<[number, number]>>;

/**
 * Checks if a DrawFeature has a point or vertex at the specified location.
 * Used for sticky selection - prioritizing already-selected features at a click location.
 *
 * @param feature - The DrawFeature to check
 * @param point - The coordinates to check [lng, lat]
 * @param tolerance - The tolerance for coordinate matching (default: 0.000001)
 * @returns true if the feature has a point/vertex at the location
 */
export function isFeatureAtLocation(feature: DrawFeature, point: [number, number], tolerance = 0.000001): boolean {
  if (feature.type === "Point") {
    const coords = feature.getCoordinates() as [number, number];
    return Math.abs(coords[0] - point[0]) < tolerance && Math.abs(coords[1] - point[1]) < tolerance;
  } else if (feature.type === "LineString") {
    const coords = feature.getCoordinates() as Array<[number, number]>;
    return coords.some((c) => Math.abs(c[0] - point[0]) < tolerance && Math.abs(c[1] - point[1]) < tolerance);
  } else if (feature.type === "Polygon") {
    const coords = feature.getCoordinates() as Array<Array<[number, number]>>;
    return coords.some((ring) => ring.some((c) => Math.abs(c[0] - point[0]) < tolerance && Math.abs(c[1] - point[1]) < tolerance));
  }

  return false;
}

/**
 * Checks if a feature is within the editable scope based on multi-layer editing configuration.
 *
 * Rules:
 * 1. If editingLayerIds is undefined, all features are editable (default behavior)
 * 2. If editingLayerIds is empty, no features are editable
 * 3. If feature's layerId is in editingLayerIds, it's editable
 * 4. If feature belongs to an editable sublayer (parentLayerId matches and passes filter), it's editable
 *
 * @param feature - The feature to check (can be a Feature or object with properties)
 * @param editingLayerIds - Array of editable layer IDs, or undefined for no filtering
 * @param editableSublayers - Array of editable sublayer configurations
 * @returns true if the feature can be edited, false otherwise
 */
export function isFeatureInEditableScope(
  feature: Feature | { properties?: Record<string, unknown> },
  editingLayerIds: string[] | undefined,
  editableSublayers: Array<{ parentLayerId: string; filter?: unknown[] }> = []
): boolean {
  // If editingLayerIds is not defined, all features are editable (default behavior)
  if (editingLayerIds === undefined) {
    return true;
  }

  // If no layers are editable, no features can be selected
  if (editingLayerIds.length === 0) {
    return false;
  }

  // Extract layerId from feature properties (try both layerId and user_layerId)
  const featureLayerId = feature.properties?.layerId || feature.properties?.user_layerId;
  if (!featureLayerId) {
    return false;
  }

  // Check if this is a regular layer (directly in editingLayerIds)
  if (editingLayerIds.includes(featureLayerId)) {
    // If there are editable sublayers for this parent, the feature must pass
    // at least one sublayer filter (a disabled sublayer won't have an entry here,
    // so its features are correctly excluded)
    const sublayersForParent = editableSublayers.filter((sub) => sub.parentLayerId === featureLayerId);
    if (sublayersForParent.length > 0) {
      return sublayersForParent.some((sublayer) => {
        if (sublayer.filter && sublayer.filter.length > 0) {
          return evaluateFilterExpression(sublayer.filter, feature as Feature);
        }
        return true;
      });
    }
    return true;
  }

  // Check if this feature belongs to an editable sublayer
  // Find editable sublayers that match this feature's parent layer
  const matchingSublayers = editableSublayers.filter((sub) => sub.parentLayerId === featureLayerId);

  if (matchingSublayers.length === 0) {
    return false; // Not in editingLayerIds and no editable sublayers for this parent layer
  }

  // Check if the feature matches any of the editable sublayers' filters
  return matchingSublayers.some((sublayer) => {
    if (sublayer.filter && sublayer.filter.length > 0) {
      return evaluateFilterExpression(sublayer.filter, feature as Feature);
    }
    // No filter means all features from this parent layer are editable
    return true;
  });
}

/**
 * Determines which feature should be selected at a click location using sticky selection logic.
 *
 * Sticky selection rules:
 * 1. Select the topmost feature by default (Mapbox's default behavior)
 * 2. BUT if there's already a selected feature/vertex at that location, prioritize it
 *
 * This allows users to continue interacting with already-selected features even when
 * they're colocated with features from other layers.
 *
 * Multi-layer selection:
 * - Features are selectable if their layerId is in editingLayerIds (regular layers)
 * - OR if they match any editable sublayer filter in editOptions.editableSublayers
 * - This allows selecting/editing features from multiple editable layers simultaneously
 *
 * Note that this relies on features having a `layerId` property which is set when
 * the feature is created.
 */
export function getFeatureTarget(
  this: DrawCustomModeThis,
  originalEvent: MapboxDraw.MapMouseEvent,
  opts: MapboxDraw.MapboxDrawOptions
): Feature | undefined {
  const { editOptions } = opts;

  // If editingLayerIds is defined (even if empty), use multi-layer selection logic
  if (editOptions?.editingLayerIds !== undefined) {
    const { editingLayerIds, editableSublayers = [] } = editOptions;

    // If no layers are editable, no features can be selected
    if (editingLayerIds.length === 0) {
      return undefined;
    }

    // Get all features at the click location that are from editable layers
    const features = this.featuresAt(originalEvent, null).filter((feature) => {
      // @ts-expect-error featureTarget is defined as MapboxDraw.DrawFeature although it is a GeoJSON Feature.
      const event = { ...originalEvent, featureTarget: feature } as MapboxDraw.MapMouseEvent;

      // Get the feature ID - if it's a vertex or midpoint, get the parent feature ID
      const featureId = isVertex(event) || isMidpoint(event) ? feature.properties?.parent : feature.properties?.id;
      if (!featureId) return false;

      // Get the actual DrawFeature to access properties without user_ prefix
      const drawFeature = this.getFeature(featureId);
      if (!drawFeature) return false;

      const featureTarget = drawFeature.toGeoJSON() as Feature;

      // Use shared helper to check if feature is in editable scope
      return isFeatureInEditableScope(featureTarget, editingLayerIds, editableSublayers);
    });

    if (features.length === 0) return undefined;

    // Sticky selection: check if any currently selected feature is at this location
    const selectedFeatures = this.getSelected();
    if (selectedFeatures.length > 0) {
      const clickedPoint = [originalEvent.lngLat.lng, originalEvent.lngLat.lat] as [number, number];

      // Check if any selected feature has a point/vertex at the clicked location
      for (const selected of selectedFeatures) {
        const matchingFeature = features.find((f) => {
          const fId = f.properties?.id || f.properties?.parent;
          return fId === selected.id;
        });

        if (matchingFeature && isFeatureAtLocation(selected, clickedPoint)) {
          return matchingFeature;
        }
      }
    }

    // No selected feature at this location, return topmost (Mapbox default behavior)
    return features[0];
  }

  // If no editingLayerIds are set then just return the original featureTarget.
  // @ts-expect-error featureTarget is defined as MapboxDraw.DrawFeature although it is a GeoJSON Feature.
  return originalEvent.featureTarget;
}

/** Returns the feature target captured on mousedown if its draw feature still exists. */
export function resolveMouseDownTarget(this: DrawCustomModeThis, mouseDownTarget: Feature | undefined): Feature | undefined {
  const featureId = mouseDownTarget?.properties?.parent ?? mouseDownTarget?.properties?.id;
  return featureId && this.getFeature(featureId) ? mouseDownTarget : undefined;
}

/**
 * Removes the horizontal and vertical snapping guides. These guides are added during the setup of the snap modes but we don't use them in our implementation so we remove them to avoid having to delete them later.
 * @param this
 */
export function removeGuides(this: DrawCustomModeThis): void {
  this.deleteFeature(Utils.IDS.HORIZONTAL_GUIDE, { silent: true });
  this.deleteFeature(Utils.IDS.VERTICAL_GUIDE, { silent: true });
}

/**
 * Checks if the given coordinates are valid (not null, undefined, or NaN).
 */
export function isValidCoordinate(lng?: number, lat?: number): boolean {
  return lng != null && lat != null && !Number.isNaN(lng) && !Number.isNaN(lat);
}

/**
 * Whether a click in a point draw mode should place a point.
 */
export function canPlacePoint(state: { snappedLng?: number; snappedLat?: number }, e: MapboxDraw.MapMouseEvent): boolean {
  // `detail` is the click count (1 for a single click, 2 for a double-click, and so on). Only the first click places a point.
  return isValidCoordinate(state.snappedLng, state.snappedLat) && (e.originalEvent?.detail ?? 0) <= 1;
}

/**
 * Ring/coord indices of every vertex in the polygon that sits on `vertexCoords`, excluding ring closure points.
 */
function findColocatedPolygonVertices(
  polygonCoords: PolygonCoords,
  vertexCoords: [number, number]
): Array<{ ringIndex: number; coordIndex: number }> {
  const matches: Array<{ ringIndex: number; coordIndex: number }> = [];

  polygonCoords.forEach((ring, ringIndex) => {
    const lastIndex = ring.length - 1;
    ring.forEach((vertexCoord, coordIndex) => {
      if (Math.abs(vertexCoord[0] - vertexCoords[0]) < 0.000001 && Math.abs(vertexCoord[1] - vertexCoords[1]) < 0.000001) {
        // Skip the last vertex if it's the same as first (closure point)
        // We'll handle closure when moving the first vertex
        if (coordIndex === lastIndex && lastIndex > 0) {
          return;
        }

        matches.push({ ringIndex, coordIndex });
      }
    });
  });

  return matches;
}

/*
 * Find all features colocated at a specific coordinate location.
 * Checks Points at the exact location, and LineString/Polygon/MultiPolygon vertices at the location.
 *
 * @param vertexCoords - The coordinates to check for colocation
 * @param nearbyFeatures - Features near the location (from featuresAt query)
 * @param excludeFeatureId - Feature ID to exclude from results (typically the feature being dragged)
 * @param allowedLayerIds - Optional array of layer IDs to filter by (only features from these layers will be included)
 * @param getFeature - Function to get the actual DrawFeature with current coordinates
 * @returns Array of colocated features with their type and vertex indices
 */
export function findColocatedFeaturesAtLocation(
  vertexCoords: [number, number],
  nearbyFeatures: Feature[],
  excludeFeatureId: string | number,
  allowedLayerIds: string[] | undefined,
  getFeature: (id: string | number) => DrawFeature | undefined
): ColocatedFeature[] {
  const colocated: ColocatedFeature[] = [];

  nearbyFeatures.forEach((f) => {
    // Exclude the current feature and only check features with IDs
    if (!f.properties?.id || f.properties.id === excludeFeatureId) return;

    // Filter by layer ID if allowedLayerIds is provided
    if (allowedLayerIds && allowedLayerIds.length > 0) {
      const featureLayerId = f.properties.layerId || f.properties.user_layerId;
      if (!featureLayerId || !allowedLayerIds.includes(featureLayerId)) return;
    }

    const fId = f.properties.id;

    // Get the actual DrawFeature to ensure we have current coordinates
    const drawFeature = getFeature(fId);
    if (!drawFeature) return;

    if (drawFeature.type === "Point") {
      const coords = drawFeature.getCoordinates() as [number, number];
      // Check if coordinates match (within a small tolerance)
      if (Math.abs(coords[0] - vertexCoords[0]) < 0.000001 && Math.abs(coords[1] - vertexCoords[1]) < 0.000001) {
        // Check if not already added
        if (!colocated.some((c) => c.id === fId && c.type === "Point")) {
          colocated.push({ id: fId, type: "Point" });
        }
      }
    } else if (drawFeature.type === "LineString") {
      // For LineStrings, check each vertex
      const coords = drawFeature.getCoordinates() as Array<[number, number]>;
      coords.forEach((vertexCoord, index) => {
        if (Math.abs(vertexCoord[0] - vertexCoords[0]) < 0.000001 && Math.abs(vertexCoord[1] - vertexCoords[1]) < 0.000001) {
          // Check if not already added
          if (!colocated.some((c) => c.id === fId && c.type === "LineString" && c.vertexIndex === index)) {
            colocated.push({ id: fId, type: "LineString", vertexIndex: index });
          }
        }
      });
    } else if (drawFeature.type === "Polygon") {
      // For Polygons, check each vertex in each ring
      const coords = drawFeature.getCoordinates() as PolygonCoords;
      findColocatedPolygonVertices(coords, vertexCoords).forEach(({ ringIndex, coordIndex }) => {
        // Check if not already added
        if (!colocated.some((c) => c.id === fId && c.type === "Polygon" && c.ringIndex === ringIndex && c.coordIndex === coordIndex)) {
          colocated.push({ id: fId, type: "Polygon", ringIndex, coordIndex });
        }
      });
    } else if (drawFeature.type === "MultiPolygon") {
      // For MultiPolygons, check each vertex in each ring of each polygon
      const coords = drawFeature.getCoordinates() as PolygonCoords[];
      coords.forEach((polygonCoords, polygonIndex) => {
        findColocatedPolygonVertices(polygonCoords, vertexCoords).forEach(({ ringIndex, coordIndex }) => {
          // Check if not already added
          if (
            !colocated.some(
              (c) =>
                c.id === fId &&
                c.type === "MultiPolygon" &&
                c.polygonIndex === polygonIndex &&
                c.ringIndex === ringIndex &&
                c.coordIndex === coordIndex
            )
          ) {
            colocated.push({ id: fId, type: "MultiPolygon", polygonIndex, ringIndex, coordIndex });
          }
        });
      });
    }
  });

  return colocated;
}

/**
 * Offsets the vertex at `coordPath` by `delta`.
 */
function moveVertexAtPath(feature: DrawFeature, coordPath: string, delta: { lng: number; lat: number }): void {
  // Get the current coordinate value using getCoordinate (like mapbox-gl-draw does internally)
  const currentCoord = feature.getCoordinate(coordPath) as [number, number];
  feature.updateCoordinate(coordPath, currentCoord[0] + delta.lng, currentCoord[1] + delta.lat);
}

/**
 * Returns the coord path of the colocated vertex.
 */
export function getColocatedCoordPath(colocated: ColocatedFeature): string {
  switch (colocated.type) {
    case "Point":
      // The entire point
      return "";
    case "LineString":
      // The specific vertex
      return colocated.vertexIndex.toString();
    case "Polygon":
      // The specific vertex in the specific ring
      return `${colocated.ringIndex}.${colocated.coordIndex}`;
    case "MultiPolygon":
      // The specific vertex in the specific ring of the specific polygon
      return `${colocated.polygonIndex}.${colocated.ringIndex}.${colocated.coordIndex}`;
    default:
      return assertNever(colocated);
  }
}

/**
 * Other vertices of the feature that sit on the vertex at `coordPath`.
 */
export function findSelfColocatedVertices(feature: DrawFeature, coordPath: string): ColocatedFeature[] {
  const vertexCoords = getVertexCoordinates(feature, coordPath);
  if (!vertexCoords) return [];

  let colocated: ColocatedFeature[] = [];
  if (feature.type === "Polygon") {
    colocated = findColocatedPolygonVertices(feature.getCoordinates() as PolygonCoords, vertexCoords).map(({ ringIndex, coordIndex }) => ({
      id: String(feature.id),
      type: "Polygon" as const,
      ringIndex,
      coordIndex,
    }));
  } else if (feature.type === "MultiPolygon") {
    colocated = (feature.getCoordinates() as PolygonCoords[]).flatMap((polygonCoords, polygonIndex) =>
      findColocatedPolygonVertices(polygonCoords, vertexCoords).map(({ ringIndex, coordIndex }) => ({
        id: String(feature.id),
        type: "MultiPolygon" as const,
        polygonIndex,
        ringIndex,
        coordIndex,
      }))
    );
  }

  return colocated.filter((c) => getColocatedCoordPath(c) !== coordPath);
}

/**
 * Move all colocated features by the specified delta.
 * For Points, moves the entire point.
 * For LineStrings, Polygons and MultiPolygons, only moves the specific colocated vertex.
 *
 * @param colocatedFeatures - Array of colocated features to move
 * @param delta - Movement delta (lng, lat)
 * @param getFeature - Function to retrieve a feature by ID
 */
export function moveColocatedFeatures(
  colocatedFeatures: ColocatedFeature[],
  delta: { lng: number; lat: number },
  getFeature: (id: string | number) => DrawFeature | undefined
): void {
  colocatedFeatures.forEach((colocated) => {
    const feature = getFeature(colocated.id);
    if (!feature) return;

    moveVertexAtPath(feature, getColocatedCoordPath(colocated), delta);
  });
}

/**
 * Filters features that can be edited (moved, deleted, modified) based on multi-layer editing configuration.
 *
 * When multi-layer editing is enabled (allLayers is defined):
 * - Features from regular layers: layerId must be in editingLayerIds
 * - Features from sublayers: layerId must match an editable sublayer's parent and pass its filter
 * - All features from editingLayerIds can be edited simultaneously
 *
 * When multi-layer editing is NOT enabled (allLayers is undefined):
 * - All features can be edited (default behavior)
 *
 * @param options - MapboxDraw options containing editOptions configuration
 * @param features - Array of features to filter
 * @returns Filtered array of features that can be edited
 */
export function filterEditableFeatures(options: MapboxDraw.MapboxDrawOptions, features: Feature[]): Feature[] {
  const { editOptions } = options;
  const allLayers = editOptions?.allLayers;
  const editingLayerIds = editOptions?.editingLayerIds;
  const editableSublayers = editOptions?.editableSublayers || [];

  // If multi-layer editing is enabled AND editingLayerIds is defined, filter based on editingLayerIds
  if (allLayers && allLayers.length > 0 && editingLayerIds !== undefined) {
    return features.filter((f) => isFeatureInEditableScope(f, editingLayerIds, editableSublayers));
  }

  // Default behavior: all features can be edited
  return features;
}

/**
 * Looks up a ring vertex of a polygon, returning null for non-integer or out-of-range indices.
 */
function getPolygonVertex(polygonCoords: PolygonCoords, ringIndex: number, coordIndex: number): [number, number] | null {
  if (Number.isNaN(ringIndex) || !Number.isInteger(ringIndex) || Number.isNaN(coordIndex) || !Number.isInteger(coordIndex)) {
    return null;
  }

  if (ringIndex < 0 || ringIndex >= polygonCoords.length) return null;

  const ringCoords = polygonCoords[ringIndex];
  if (coordIndex < 0 || coordIndex >= ringCoords.length) return null;

  return ringCoords[coordIndex] ?? null;
}

/**
 * Extracts the coordinates of a specific vertex from a feature based on its coordinate path.
 *
 * @param feature - The DrawFeature to extract coordinates from
 * @param coordPath - The coordinate path (e.g., "0" for LineString, "0.1" for Polygon, "0.0.1" for MultiPolygon)
 * @returns The vertex coordinates as [lng, lat], or null if not found
 */
export function getVertexCoordinates(feature: DrawFeature, coordPath: string): [number, number] | null {
  const parts = coordPath.split(".");
  const coords = feature.getCoordinates();

  if (feature.type === "Point") {
    return coords as [number, number];
  } else if (feature.type === "LineString") {
    if (parts.length !== 1) return null;

    const index = Number(parts[0]);
    if (Number.isNaN(index) || !Number.isInteger(index)) return null;

    const lineCoords = coords as Array<[number, number]>;
    if (index < 0 || index >= lineCoords.length) return null;

    return lineCoords[index] ?? null;
  } else if (feature.type === "Polygon") {
    if (parts.length !== 2) return null;

    const [ringIndex, coordIndex] = parts.map(Number);
    return getPolygonVertex(coords as PolygonCoords, ringIndex, coordIndex);
  } else if (feature.type === "MultiPolygon") {
    if (parts.length !== 3) return null;

    const [polygonIndex, ringIndex, coordIndex] = parts.map(Number);
    const polygonCoords = Number.isInteger(polygonIndex) ? (coords as PolygonCoords[])[polygonIndex] : undefined;
    return polygonCoords ? getPolygonVertex(polygonCoords, ringIndex, coordIndex) : null;
  }

  return null;
}

/**
 * Checks if a selected vertex is at the first or last position of a LineString.
 *
 * @param feature - The DrawFeature to check (must be a LineString)
 * @param coordPath - The coordinate path of the selected vertex
 * @returns true if the vertex is at the start or end of the LineString, false otherwise
 */
export function isLineStringEndVertex(feature: DrawFeature, coordPath: string): boolean {
  if (feature.type !== "LineString") return false;

  const coords = feature.getCoordinates() as Array<[number, number]>;
  const index = parseInt(coordPath, 10);

  if (Number.isNaN(index) || index < 0 || index >= coords.length) return false;

  return index === 0 || index === coords.length - 1;
}

/**
 * Checks if a click event is on a draw feature (vertex, midpoint, or feature itself).
 *
 * @param event - The mouse event to check
 * @returns true if the click is on a draw feature, false if clicking on empty map
 */
export function isClickOnDrawFeature(event: MapboxDraw.MapMouseEvent): boolean {
  return (
    event.featureTarget?.properties?.meta === Constants.meta.VERTEX ||
    event.featureTarget?.properties?.meta === Constants.meta.MIDPOINT ||
    !!event.featureTarget?.properties?.id
  );
}

/**
 * Extends a LineString from one of its end vertices by adding a new coordinate.
 * Updates the feature's coordinates in place.
 *
 * @param feature - The LineString feature to extend
 * @param coordPath - The coordinate path of the end vertex (must be "0" or last index)
 * @param newCoords - The new coordinates to add [lng, lat]
 * @returns The new index of the end vertex after extension, or null if extension failed
 */
export function extendLineString(feature: DrawFeature, coordPath: string, newCoords: [number, number]): number | null {
  if (feature.type !== "LineString") return null;

  const coords = feature.getCoordinates() as Array<[number, number]>;
  const index = parseInt(coordPath, 10);

  // Only allow extending from first or last vertex
  if (index !== 0 && index !== coords.length - 1) return null;

  const updatedCoords = [...coords];
  if (index === 0) {
    // Extend from beginning
    updatedCoords.unshift(newCoords);
    feature.setCoordinates(updatedCoords);
    return 0; // New vertex is at index 0
  } else {
    // Extend from end
    updatedCoords.push(newCoords);
    feature.setCoordinates(updatedCoords);
    return updatedCoords.length - 1; // New vertex is at the end
  }
}

/**
 * Resolves whether topological editing should be active for a given feature and which
 * layer IDs should participate, based on both the global `topologicalEditing` toggle
 * and any per-layer `compulsoryTopologicalLayerIds`.
 *
 * - Global on: uses `editingLayerIds`, merged with compulsory IDs as superset
 * - Global off, compulsory IDs present: uses only compulsory IDs
 * - Otherwise: no topology
 */
export function resolveTopologicalLayerIds(
  options: MapboxDrawOptions,
  featureLayerId: string | undefined
): { shouldDoTopology: boolean; allowedLayerIds: string[] | undefined } {
  const layerConfig: EditingLayer | undefined = featureLayerId ? options.editOptions?.allLayers?.find((l) => l.ID === featureLayerId) : undefined;

  const compulsoryIds = layerConfig?.compulsoryTopologicalLayerIds;
  const globalTopo = options.topologicalEditing;

  const shouldDoTopology = !!(globalTopo || (compulsoryIds && compulsoryIds.length > 0));

  if (!shouldDoTopology) {
    return { shouldDoTopology: false, allowedLayerIds: undefined };
  }

  let allowedLayerIds: string[] | undefined;
  if (globalTopo) {
    allowedLayerIds = options.editOptions?.editingLayerIds;
    if (compulsoryIds && allowedLayerIds) {
      const merged = new Set([...allowedLayerIds, ...compulsoryIds]);
      allowedLayerIds = Array.from(merged);
    }
  } else {
    allowedLayerIds = compulsoryIds;
  }

  return { shouldDoTopology: true, allowedLayerIds };
}

/**
 * Hands each feature over to its layer's `onTrash` callback, where one is configured.
 * Features belonging to layers without a callback are returned so the caller can apply
 * the default delete behaviour.
 *
 * @returns The features that were not handled by a layer callback
 */
export function delegateTrashToLayers(this: DrawCustomModeThis, state: ValidationState, features: Feature[]): Feature[] {
  const allLayers = this._ctx.options.editOptions?.allLayers;
  if (!allLayers?.length) return features;

  const unhandled: Feature[] = [];
  const byLayer = new Map<EditingLayer, DrawFeature[]>();

  features.forEach((feature) => {
    const layerId = feature.properties?.layerId || feature.properties?.user_layerId;
    const layer = layerId ? allLayers.find((l) => l.ID === layerId) : undefined;
    const drawFeature = this.getFeature(feature.id || feature.properties?.id);

    if (!layer?.onTrash || !drawFeature) {
      unhandled.push(feature);
      return;
    }

    byLayer.set(layer, [...(byLayer.get(layer) ?? []), drawFeature]);
  });

  byLayer.forEach((layerFeatures, layer) => {
    layer.onTrash?.call(this, layerFeatures, state);
  });

  return unhandled;
}
